export default class strlCursor {
	constructor() {
		this.config = {
			swiperRadius: 45,
			strokeColor: '#ffffff',
			arrowSize: 0.6,
			scrub: 0.15,
			animSpeed: 0.2,
			opacitySpeed: 0.15,
			showDelayMs: 0,
			selectors: {
				canvas: '[data-cursor-canvas]',
				swipers: '[data-swiper-animate-cursor]',
			},
		};

		this.state = {
			mouseX: 0,
			mouseY: 0,
			isOnSwiper: false,
			swiperDirection: null,
		};

		this.circle = {
			x: 0,
			y: 0,
			radius: this.config.swiperRadius,
			lineWidth: 2,
		};

		this.targets = {
			radius: this.config.swiperRadius,
			lineWidth: 2,
		};

		this.opacity = 0;
		this.opacityTarget = 0;
		this.showAt = 0;
		this.rafId = null;

		this.init();
	}

	init() {
		const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		const isMobile = window.matchMedia('(pointer: coarse)').matches;

		if (prefersReduced || isMobile) {
			return;
		}

		this.canvasNode = document.querySelector(this.config.selectors.canvas);
		if (!this.canvasNode) {
			return;
		}

		this.ctx = this.canvasNode.getContext('2d');
		this.swiperContainers = document.querySelectorAll(this.config.selectors.swipers);

		this.onResize();
		this.addEventListeners();
	}

	addEventListeners() {
		window.addEventListener('resize', this.onResize, false);
		window.addEventListener('mousemove', this.onMouseMove);

		this.swiperContainers.forEach((el) => {
			el.addEventListener('mouseenter', this.handleSwiperEnter);
			el.addEventListener('mouseleave', this.handleSwiperLeave);
			el.addEventListener('click', this.handleSwiperClick, true);
		});
	}

	onResize = () => {
		if (!this.canvasNode) return;
		this.width = this.canvasNode.width = window.innerWidth;
		this.height = this.canvasNode.height = window.innerHeight;
	};

	onMouseMove = (e) => {
		this.state.mouseX = e.clientX;
		this.state.mouseY = e.clientY;
		if (!this.state.isOnSwiper) {
			return;
		}

		this.updateSwiperDirection(e);
	};

	handleSwiperEnter = (e) => {
		this.state.isOnSwiper = true;
		this.state.mouseX = e.clientX;
		this.state.mouseY = e.clientY;
		this.updateSwiperDirection(e);
	};

	handleSwiperLeave = () => {
		this.state.isOnSwiper = false;
		this.state.swiperDirection = null;
		this.fadeOut();
	};

	handleSwiperClick = (e) => {
		if (!this.state.swiperDirection || this.opacity < 0.5) {
			return;
		}

		const swiperEl = e.target?.closest('.swiper');
		const swiperInstance = swiperEl?.swiper;
		if (!swiperInstance) {
			return;
		}
		e.preventDefault();
		e.stopPropagation();

		if (this.state.swiperDirection === 'left') {
			swiperInstance.slidePrev();
		} else if (this.state.swiperDirection === 'right') {
			swiperInstance.slideNext();
		}
	};

	updateSwiperDirection = (e) => {
		if (!this.state.isOnSwiper) {
			this.state.swiperDirection = null;
			this.fadeOut();
			return;
		}

		const windowWidth = window.innerWidth;
		let found = false;
		let direction = null;

		for (const container of this.swiperContainers) {
			const rect = container.getBoundingClientRect();
			const x = e.clientX;
			const y = e.clientY;
			if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
				const center = rect.left + rect.width / 2;
				if (x < center - windowWidth / 4) {
					found = true;
					direction = 'left';
				} else if (x > center + windowWidth / 4) {
					found = true;
					direction = 'right';
				}
			}
		}

		if (!found) {
			this.state.swiperDirection = null;
			this.fadeOut();
			return;
		}

		if (!this.state.swiperDirection) {
			this.showAt = performance.now() + this.config.showDelayMs;
			this.opacity = 0;
		}

		this.state.swiperDirection = direction;
		this.opacityTarget = 1;
		this.startRender();
	};

	fadeOut = () => {
		this.opacityTarget = 0;
		this.startRender();
	};

	lerp(a, b, n) {
		return (1 - n) * a + n * b;
	}

	updateOpacity = () => {
		if (this.opacityTarget > 0 && performance.now() < this.showAt) {
			this.opacity = 0;
			return;
		}

		this.opacity = this.lerp(this.opacity, this.opacityTarget, this.config.opacitySpeed);
	};

	drawArrow = (x, y, radius, direction) => {
		const size = radius * this.config.arrowSize;
		this.ctx.lineWidth = this.circle.lineWidth;
		this.ctx.beginPath();
		const dir = direction === 'left' ? -1 : 1;

		this.ctx.moveTo(x + size * 0.2 * dir, y);
		this.ctx.lineTo(x - size * 0.2 * dir, y - size * 0.35);

		this.ctx.moveTo(x + size * 0.2 * dir, y);
		this.ctx.lineTo(x - size * 0.2 * dir, y + size * 0.35);

		this.ctx.moveTo(x + size * 0.2 * dir, y);
		this.ctx.lineTo(x - size * 1.0 * dir, y);

		this.ctx.stroke();
		this.ctx.closePath();
	};

	startRender = () => {
		if (this.rafId) {
			return;
		}
		this.rafId = requestAnimationFrame(this.render);
	};

	stopRender = () => {
		if (this.rafId) {
			cancelAnimationFrame(this.rafId);
			this.rafId = null;
		}
		if (this.ctx) {
			this.ctx.clearRect(0, 0, this.width, this.height);
		}
	};

	render = () => {
		const isActive = this.state.isOnSwiper && this.state.swiperDirection;

		if (!isActive && this.opacity <= 0.01 && this.opacityTarget <= 0) {
			this.opacity = 0;
			this.stopRender();
			return;
		}

		if (isActive) {
			this.circle.x = this.lerp(this.circle.x, this.state.mouseX, this.config.scrub);
			this.circle.y = this.lerp(this.circle.y, this.state.mouseY, this.config.scrub);
			this.circle.radius = this.lerp(this.circle.radius, this.targets.radius, this.config.animSpeed);
			this.circle.lineWidth = this.lerp(this.circle.lineWidth, this.targets.lineWidth, this.config.animSpeed);
		}
		this.updateOpacity();

		this.ctx.clearRect(0, 0, this.width, this.height);

		if (this.opacity <= 0.01) {
			this.rafId = requestAnimationFrame(this.render);
			return;
		}

		this.ctx.save();
		this.ctx.globalAlpha = this.opacity;
		this.ctx.strokeStyle = this.config.strokeColor;

		this.ctx.beginPath();
		this.ctx.arc(this.circle.x, this.circle.y, this.circle.radius, 0, Math.PI * 2, false);
		this.ctx.lineWidth = this.circle.lineWidth;
		this.ctx.stroke();
		this.ctx.closePath();

		if (this.state.swiperDirection) {
			this.drawArrow(
				this.circle.x + ('left' === this.state.swiperDirection ? -10 : 10),
				this.circle.y,
				this.circle.radius,
				this.state.swiperDirection
			);
		}

		this.ctx.restore();

		this.rafId = requestAnimationFrame(this.render);
	};
}
